import { useState, useEffect } from "react";
import { Zap, Loader2, CheckCircle2, Info, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import {
  calculateTokensFromSol,
  calculateSolFromTokens,
  purchaseLp,
  PRESALE_CONFIG,
  isPresaleActive,
} from "@/lib/presale";
import { getPulsePriceInUsdc } from "@/lib/pricing";
import { getCachedUserInvestment } from "@/lib/lpTracker";

export function PresalePurchase() {
  const { connected, publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { setVisible } = useWalletModal();
  const [solAmount, setSolAmount] = useState("");
  const [tokenAmount, setTokenAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [txHash, setTxHash] = useState("");
  const [presalePriceUsdc, setPresalePriceUsdc] = useState<number | null>(null);
  const [initialPriceUsdc, setInitialPriceUsdc] = useState<number | null>(null);
  const [userInvestment, setUserInvestment] = useState<number | null>(null);
  const [presaleActive, setPresaleActive] = useState(isPresaleActive());

  const formatPrice = (price: number): string => {
    if (price === 0) return "0";
    if (price >= 1) {
      // For numbers >= 1, show first decimal place
      return price.toFixed(1);
    }

    // For numbers < 1, convert to fixed decimal string to avoid scientific notation
    // Use enough precision to capture the first non-zero digit
    const priceStr = price.toFixed(20);
    const decimalIndex = priceStr.indexOf('.');

    if (decimalIndex === -1) {
      return price.toFixed(1);
    }

    // Find first non-zero digit after decimal
    let firstNonZeroIndex = -1;
    for (let i = decimalIndex + 1; i < priceStr.length; i++) {
      if (priceStr[i] !== '0') {
        firstNonZeroIndex = i;
        break;
      }
    }

    if (firstNonZeroIndex === -1) {
      return "0";
    }

    // Show from decimal point to one digit after first non-zero
    // Example: 0.000123 -> first non-zero at index 4, show 5 decimal places = 0.0001
    const digitsAfterDecimal = firstNonZeroIndex - decimalIndex;
    const precision = digitsAfterDecimal + 1;

    return price.toFixed(Math.min(precision, 20));
  };

  const handleSolAmountChange = (value: string) => {
    // Allow empty input
    if (value === "") {
      setSolAmount("");
      setTokenAmount("");
      return;
    }

    // Allow typing decimal point and numbers
    // Match valid decimal numbers: optional digits, optional decimal point, optional digits after
    const validPattern = /^(\d+\.?\d*|\.\d*)$/;

    if (validPattern.test(value)) {
      setSolAmount(value);
      // Only update token amount if we have a valid number
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 0) {
        const tokens = calculateTokensFromSol(numValue);
        setTokenAmount(tokens.toLocaleString());
      } else {
        setTokenAmount("");
      }
    }
  };

  useEffect(() => {
    // Only update token amount when solAmount is a valid number
    if (solAmount && solAmount !== "") {
      const numValue = parseFloat(solAmount);
      if (!isNaN(numValue) && numValue > 0) {
        const tokens = calculateTokensFromSol(numValue);
        setTokenAmount(tokens.toLocaleString());
      }
    } else if (solAmount === "") {
      setTokenAmount("");
    }
  }, [solAmount]);

  // Fetch USDC prices on mount
  useEffect(() => {
    async function fetchPrices() {
      const presalePrice = await getPulsePriceInUsdc(PRESALE_CONFIG.pricePerToken);
      const initialPrice = await getPulsePriceInUsdc(PRESALE_CONFIG.initialListingPrice);
      setPresalePriceUsdc(presalePrice);
      setInitialPriceUsdc(initialPrice);
    }
    fetchPrices();

    // Refresh prices every minute
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch user investment from transaction history
  useEffect(() => {
    if (!connected || !publicKey || !connection) {
      setUserInvestment(null);
      return;
    }

    // Capture values for use in async function
    const userPubkey = publicKey;
    const conn = connection;

    async function fetchInvestment() {
      try {
        const investment = await getCachedUserInvestment(userPubkey, conn);
        setUserInvestment(investment);
      } catch (error) {
        console.error("Error fetching user investment:", error);
        setUserInvestment(0);
      }
    }

    fetchInvestment();

    // Refresh investment every 30 seconds
    const interval = setInterval(fetchInvestment, 30000);
    return () => clearInterval(interval);
  }, [connected, publicKey, connection]);

  // Check presale status periodically
  useEffect(() => {
    setPresaleActive(isPresaleActive());
    const interval = setInterval(() => {
      setPresaleActive(isPresaleActive());
    }, 1000); // Check every second
    return () => clearInterval(interval);
  }, []);

  const handlePurchase = async () => {
    if (!connected || !publicKey) {
      setVisible(true);
      return;
    }

    if (!connection || !sendTransaction) {
      setError("Wallet connection not ready");
      return;
    }

    const amount = parseFloat(solAmount);
    if (!amount || amount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (amount < PRESALE_CONFIG.minPurchase) {
      setError(`Minimum purchase is ${PRESALE_CONFIG.minPurchase} SOL`);
      return;
    }

    if (amount > PRESALE_CONFIG.maxPurchase) {
      setError(`Maximum purchase is ${PRESALE_CONFIG.maxPurchase} SOL`);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const result = await purchaseLp(amount, publicKey, connection, sendTransaction);
      if (result.success) {
        setSuccess(true);
        setTxHash(result.txHash || "");
        setSolAmount("");
        setTokenAmount("");
        // Refresh user investment after successful purchase
        // publicKey is guaranteed to be non-null here due to check at start of function
        if (connection) {
          getCachedUserInvestment(publicKey!, connection)
            .then((investment) => setUserInvestment(investment))
            .catch((err) => console.error("Error refreshing investment:", err));
        }
        setTimeout(() => {
          setSuccess(false);
          setTxHash("");
        }, 8000);
      } else {
        setError(result.error || "Purchase failed");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const setMaxAmount = () => {
    setSolAmount(PRESALE_CONFIG.maxPurchase.toString());
  };

  const quickAmounts = [0.5, 1, 5, 10];

  return (
    <div id="presale-purchase" className="w-full py-12 md:py-16 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            <Zap className="w-8 h-8 text-purple-400" />
            Purchase $PULSE
          </h2>
          <p className="text-gray-400 mb-3">
            {presaleActive
              ? "Secure your tokens at presale price. 48 hours only - starts Monday!"
              : "The presale period has ended. Thank you for your participation!"}
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600/20 to-violet-600/20 border border-purple-500/30 rounded-full">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-purple-300">40% Discount</span>
            <span className="text-xs text-gray-400">vs. initial listing price</span>
          </div>
        </div>

        <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300">
          <CardContent className="pt-6 px-4 md:px-6">
            {!presaleActive && (
              <div className="mb-6 p-4 bg-amber-500/20 border border-amber-500/50 rounded-lg animate-fade-in">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-amber-400 font-semibold mb-1">Presale Period Ended</p>
                    <p className="text-sm text-amber-300">
                      The presale has concluded. Thank you to all participants! Tokens will be distributed soon.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg animate-fade-in">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-green-400 font-semibold mb-1">Purchase Successful!</p>
                    <p className="text-sm text-green-300">
                      Transaction: <span className="font-mono text-xs">{txHash}</span>
                    </p>
                    <p className="text-xs text-green-400/80 mt-2">
                      Your tokens will be available after the presale ends.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg animate-fade-in">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-red-400">{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* SOL Input */}
              <div className="space-y-3">
                <Label htmlFor="sol-amount" className="text-gray-300 text-sm md:text-base">
                  Amount (SOL)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="sol-amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.0"
                    value={solAmount}
                    onChange={(e) => handleSolAmountChange(e.target.value)}
                    className="bg-gray-900/50 border-purple-500/30 text-white text-lg focus:border-purple-500 focus:ring-purple-500/20"
                  />
                  <Button
                    variant="outline"
                    onClick={setMaxAmount}
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50 transition-all whitespace-nowrap"
                  >
                    Max
                  </Button>
                </div>

                {/* Quick amount buttons */}
                <div className="flex flex-wrap gap-2">
                  {quickAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant="outline"
                      size="sm"
                      onClick={() => setSolAmount(amount.toString())}
                      className="border-purple-500/20 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/40 text-xs"
                    >
                      {amount} SOL
                    </Button>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Info className="w-3 h-3" />
                  <span>
                    Min: {PRESALE_CONFIG.minPurchase} SOL | Max: {PRESALE_CONFIG.maxPurchase} SOL
                  </span>
                </div>
              </div>

              {/* Token Amount Display */}
              <div className="space-y-3">
                <Label className="text-gray-300 text-sm md:text-base">You will receive</Label>
                <div className="p-5 bg-gradient-to-br from-purple-900/30 to-violet-900/30 border border-purple-500/30 rounded-lg relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent shimmer-animation" />
                  <div className="flex items-center justify-between relative z-10">
                    <span className="text-3xl md:text-4xl font-bold text-purple-400">
                      ~{tokenAmount || "0"}
                    </span>
                    <span className="text-lg md:text-xl text-gray-300 font-semibold">$PULSE</span>
                  </div>
                </div>
              </div>

              {/* Price Info */}
              <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <span className="text-gray-400">Presale price:</span>
                    <div className="text-lg font-bold text-purple-400 mt-1">
                      $0.0001
                    </div>
                    <div className="text-xs text-purple-300 mt-1">
                      {formatPrice(PRESALE_CONFIG.pricePerToken)} SOL • 40% discount
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-400">Initial listing:</span>
                    <div className="text-lg font-bold text-gray-400 line-through mt-1">
                      $0.00025
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatPrice(PRESALE_CONFIG.initialListingPrice)} SOL • After presale
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t border-purple-500/20">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Your investment:</span>
                    <span className="text-lg font-bold text-violet-400">
                      {userInvestment !== null
                        ? `${userInvestment.toFixed(4)} SOL`
                        : connected
                          ? "Loading..."
                          : "0.0 SOL"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Wallet Info */}
              {connected && publicKey && (
                <div className="p-4 bg-gray-900/50 border border-purple-500/30 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Connected wallet:</span>
                    <span className="text-purple-400 font-mono text-xs md:text-sm">
                      {publicKey.toString().slice(0, 6)}...{publicKey.toString().slice(-6)}
                    </span>
                  </div>
                </div>
              )}

              {/* Purchase Button */}
              <Button
                onClick={handlePurchase}
                disabled={!presaleActive || loading || !solAmount || parseFloat(solAmount) <= 0}
                className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white text-base md:text-lg py-6 md:py-7 rounded-lg shadow-lg shadow-purple-500/50 transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {!presaleActive ? (
                  <>
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Presale Ended
                  </>
                ) : loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing Transaction...
                  </>
                ) : connected ? (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Purchase $PULSE
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Connect Wallet to Purchase
                  </>
                )}
              </Button>

              {/* Info Box */}
              <div className="space-y-3">
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-300">
                      <p className="font-semibold mb-1">Presale Details</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li><strong>48 hours only</strong> - Starts Monday, ends Wednesday</li>
                        <li><strong>40% discount</strong> compared to initial listing price</li>
                        <li><strong>100% of presale funds</strong> go directly into liquidity pool</li>
                        <li>Tokens will be distributed after presale completion</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-purple-300">
                      <p className="font-semibold mb-1">Important</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>All transactions are final and non-refundable</li>
                        <li>Ensure you have sufficient SOL for gas fees</li>
                        <li>Maximum purchase: {PRESALE_CONFIG.maxPurchase} SOL per wallet</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
